import React from 'react';
import { Box, Typography, Avatar, Checkbox, IconButton, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { HourglassEmpty, DeleteOutline, EditOutlined } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { MockEvent } from './types';
import { CATEGORY_META } from './constants';

interface EventRowProps {
  event: MockEvent;
  onClick: () => void;
  onToggleComplete: (isCompleted: boolean) => void;
  onDelete: () => void;
  onEdit: () => void;
}

export const EventRow: React.FC<EventRowProps> = ({ event, onClick, onToggleComplete, onDelete, onEdit }) => {
  const meta = CATEGORY_META[event.type];
  const details = event.raw?.details as any;
  const rawInput: string | undefined = details?.rawInput;
  const isPending = event.raw?.nutritionStatus === 'pending';

  // Only show checkbox for specific types
  const showCheckbox = ['task', 'medication', 'workout', 'food', 'liquid'].includes(event.type);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, py: 1.25, px: 1,
      minHeight: 68,
      borderRadius: 1,
      opacity: event.completed ? 0.6 : 1,
      cursor: 'pointer',
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      {showCheckbox && (
        <Checkbox
          size="small"
          checked={event.completed}
          onChange={(e) => {
            e.stopPropagation();
            onToggleComplete(e.target.checked);
          }}
          sx={{ 
            p: 0.5,
            color: meta.color,
            '&.Mui-checked': { color: meta.color }
          }}
        />
      )}
      <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(meta.color, 0.12), color: meta.color, flexShrink: 0 }}>
          {meta.icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{
              wordBreak: 'break-word',
              color: event.completed ? 'text.secondary' : 'text.primary'
            }}>
              {event.title}
            </Typography>
            {event.raw?.source === 'ai' && (
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '0.6rem !important' }} />}
                label="AI"
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiChip-icon': { color: 'primary.contrastText', ml: 0.5 },
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
            {(event.raw?.energyImpact ?? 0) > 0 && (
              <Typography variant="caption" sx={{
                fontWeight: 600,
                color: event.type === 'food' ? 'success.main' : 'warning.main',
              }}>
                {event.type === 'food' ? '+' : '−'}{event.raw!.energyImpact} ккал
              </Typography>
            )}
          </Box>
          {rawInput && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mt: 0.25,
            }}>
              {rawInput}
            </Typography>
          )}
          {isPending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25 }}>
              <HourglassEmpty sx={{ fontSize: 11, color: 'text.disabled' }} />
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                нутрієнти розраховуються
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, width: 38, textAlign: 'right' }}>
          {event.time}
        </Typography>
      </Box>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' }, flexShrink: 0 }}
      >
        <EditOutlined fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, flexShrink: 0 }}
      >
        <DeleteOutline fontSize="small" />
      </IconButton>
    </Box>
  );
};
